import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Quote } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Overline, GoldRule } from "@/components/Bits";
import { EditorialMarquee } from "@/components/EditorialMarquee";
import { PORTRAIT } from "@/lib/assets";
import api from "@/lib/api";

export default function About() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/about").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  const d = data || {};
  const roles = d.roles || [];
  const story = d.story || [];
  const milestones = d.milestones || [];
  const values = d.core_values || [];
  const pillars = d.ministry_pillars || [];
  const credentials = d.credentials || [];
  const gallery = d.gallery || [];

  return (
    <div data-testid="about-page">
      <PageHero
        overline="Kingdom Leader · Pastor · Music Minister · Business Strategist · AI Educator"
        title={<>A life devoted to<br /><span className="font-accent text-gold-gradient italic">Kingdom transformation.</span></>}
        subtitle={d.positioning || "Equipping individuals, churches, entrepreneurs, and organizations to lead with biblical wisdom, excellence, and innovation."}
      />

      {/* Portrait + roles */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 grid gap-16 lg:grid-cols-[1fr_1.15fr] lg:items-center">
        <Reveal>
          <div className="corner-frame border border-gold-500/25 p-1.5">
            <img src={PORTRAIT} alt="Apostle Blaise Judah" className="aspect-[4/5] w-full object-cover object-[center_20%]" />
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <Overline className="mb-6">About Apostle Blaise Judah</Overline>
          <p className="font-serif text-3xl md:text-4xl leading-tight text-white">
            Founder & Senior Pastor of House of Blessing Ministries.
          </p>
          <GoldRule className="my-8" />
          <div className="flex flex-wrap gap-2.5">
            {roles.map((r) => (
              <span key={r} className="border border-gold-500/30 px-4 py-2 text-xs uppercase tracking-[0.15em] text-white/75">{r}</span>
            ))}
          </div>
          {d.leadership_quote && (
            <div className="mt-10 border-l-2 border-gold-500 pl-6">
              <p className="font-accent italic text-xl md:text-2xl text-white/90 leading-snug">"{d.leadership_quote}"</p>
            </div>
          )}
        </Reveal>
      </section>

      {/* Chairman's Message */}
      <section className="border-y border-gold-500/20 bg-ink-royal/15" data-testid="chairman-message">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 grid gap-14 lg:grid-cols-[1fr_1.3fr] lg:items-start">
          <Reveal>
            <div className="lg:sticky lg:top-28">
              <div className="corner-frame border border-gold-500/25 p-1.5">
              <img src={PORTRAIT} alt="Apostle Blaise Judah" className="aspect-[4/5] w-full object-cover object-[center_20%]" />
            </div>
            <div className="mt-6">
                <p className="font-accent italic text-2xl text-gold-300">Apostle Blaise Judah</p>
                <p className="text-xs uppercase tracking-[0.2em] text-white/55 mt-1">Founder & Senior Pastor · House of Blessing Ministries</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Overline className="mb-6">Chairman's Message</Overline>
            <p className="font-accent italic text-2xl md:text-3xl text-gold-200 leading-snug">{d.chairman_greeting}</p>
            <div className="mt-8 space-y-5">
              {(d.chairman_message || []).map((p, i) => (
                <p key={i} className={`text-white/72 leading-relaxed ${i === 0 ? "first-letter:font-serif first-letter:text-6xl first-letter:text-gold-400 first-letter:mr-3 first-letter:float-left first-letter:leading-[0.8]" : ""}`}>{p}</p>
              ))}
            </div>
            <p className="mt-8 text-white/60">{d.chairman_signoff}</p>
            <p className="mt-4 font-accent italic text-3xl text-white">Apostle Blaise Judah</p>
            <p className="text-xs uppercase tracking-[0.2em] text-gold-500 mt-1">Founder & Senior Pastor</p>
          </Reveal>
        </div>
      </section>

      {/* Our Story */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 pb-24" data-testid="about-story">
        <Reveal className="mb-12"><Overline>Our Story · The Journey of Grace and Purpose</Overline></Reveal>
        <div className="grid gap-x-16 gap-y-8 md:grid-cols-2">
          {story.map((p, i) => (
            <Reveal key={i} delay={(i % 2) * 0.08}>
              <p className="text-white/70 leading-relaxed text-[15px]">{p}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Milestones timeline */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 pb-28" data-testid="about-milestones">
        <Reveal className="mb-14"><Overline>Milestones</Overline></Reveal>
        <div className="border-t border-gold-500/20">
          {milestones.map((m, i) => (
            <Reveal key={m.year} delay={i * 0.05}>
              <div className="group grid gap-6 border-b border-gold-500/20 py-8 md:grid-cols-[160px_1fr] md:gap-14 md:items-baseline transition-colors duration-500 hover:bg-ink-paper/30">
                <span className="font-serif text-4xl md:text-5xl text-gold-gradient leading-none">{m.year}</span>
                <div className="max-w-3xl">
                  <h3 className="font-serif text-2xl md:text-3xl text-white">{m.title}</h3>
                  <p className="mt-3 text-white/65 leading-relaxed">{m.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Photo gallery */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 pb-28" data-testid="about-gallery">
        <Reveal className="mb-12"><Overline>Portrait Gallery</Overline></Reveal>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {gallery.map((g, i) => (
            <Reveal key={g.src} delay={(i % 4) * 0.06}>
              <div className={`group relative overflow-hidden border border-gold-500/20 ${i === 0 || i === 3 ? "md:row-span-2 aspect-[3/4] md:aspect-auto md:h-full" : "aspect-square"}`}>
                <img src={g.src} alt={g.label} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <EditorialMarquee text="FAITH · LEADERSHIP · INNOVATION · IMPACT" />

      {/* Ministry pillars */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-28" data-testid="about-pillars">
        <Reveal className="mb-14"><Overline>Ministry Overview</Overline></Reveal>
        <div className="grid gap-px bg-gold-500/15 border border-gold-500/15 md:grid-cols-2 lg:grid-cols-3">
          {pillars.map((p, i) => (
            <Reveal key={p.title} delay={(i % 3) * 0.06}>
              <div className="h-full bg-ink p-8 hover:bg-ink-paper transition-colors duration-500">
                <span className="font-serif text-4xl text-gold-500/60">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-6 font-serif text-2xl text-white">{p.title}</h3>
                <p className="mt-3 text-sm text-white/60 leading-relaxed">{p.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Core values */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 pb-28" data-testid="about-values">
        <Reveal className="mb-14"><Overline>Core Values</Overline></Reveal>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {values.map((v, i) => (
            <Reveal key={v.title} delay={(i % 3) * 0.05}>
              <div className="flex gap-4 border border-gold-500/20 bg-ink-paper/30 p-6 h-full">
                <span className="h-2 w-2 mt-2 shrink-0 bg-gold-500 rotate-45" />
                <div>
                  <h4 className="font-serif text-xl text-white">{v.title}</h4>
                  <p className="mt-1.5 text-sm text-white/60 leading-relaxed">{v.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Credentials */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 pb-28" data-testid="about-credentials">
        <div className="grid gap-px bg-gold-500/15 border border-gold-500/15 md:grid-cols-2">
          {credentials.map((c, i) => (
            <Reveal key={c.title} delay={(i % 2) * 0.06}>
              <div className="h-full bg-ink p-9">
                <h3 className="font-serif text-2xl text-gold-300">{c.title}</h3>
                <ul className="mt-6 space-y-3">
                  {c.items.map((it) => (
                    <li key={it} className="flex gap-3 text-sm text-white/70 leading-relaxed">
                      <span className="text-gold-500 mt-1">—</span> {it}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Mission / Vision / Promise */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 pb-28" data-testid="about-mvp">
        <div className="grid gap-6 lg:grid-cols-3">
          {[["Mission", d.mission], ["Vision", d.vision], ["Brand Promise", d.brand_promise]].map(([label, body], i) => (
            <Reveal key={label} delay={i * 0.08}>
              <div className="h-full glass corner-frame p-8">
                <Overline>{label}</Overline>
                <p className="mt-5 text-white/75 leading-relaxed">{body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Signature quote */}
      <section className="relative border-t border-gold-500/20 py-28 md:py-36 text-center">
        <Reveal className="mx-auto max-w-4xl px-6">
          <Quote size={40} className="text-gold-500 mx-auto mb-8" />
          <p className="font-accent italic text-3xl md:text-5xl leading-tight text-white">
            "{d.signature_quote}"
          </p>
          <p className="mt-10 text-xs uppercase tracking-[0.3em] text-gold-500">Apostle Blaise Judah</p>
          <div className="mt-12 flex flex-wrap justify-center gap-3">
            <Link to="/profile" className="inline-flex items-center gap-2 bg-gold-500 px-8 py-4 text-sm font-semibold text-ink hover:bg-gold-400 active:scale-[0.98] transition-[background,transform] duration-300">
              View Executive Profile <ArrowUpRight size={16} />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 border border-gold-500/40 px-8 py-4 text-sm text-white/85 hover:border-gold-400 hover:text-gold-300 transition-colors duration-300">
              Connect Today
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
